import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { firstValueFrom } from "rxjs";
import { CLAIM_STATUS, Claim, ClaimDocument } from "../models/Claim";
import { OrderStatusFilter } from "../../orders/models/filter/OrderFilter";
import { GetClaimsResponse } from "../models/GetClaimsResponse.model";
import { ClaimStatusFilter } from "../models/filter/ClaimFilter.model";
import { Logger } from "../../../infrastructure/logging/logger";
import { SearchParams } from "../../../shared/search/SearchParams";
import { isStringEmptyOrNull } from "../../../utilities/StringUtility";
import { AdminPortalAPIHttpClient } from "../../../infrastructure/http/v2/AdminPortalAPIHttpClient";

export interface GetClaimsParams {
    filter: ClaimStatusFilter;
    page: number;
    pageSize: number;
    reservationId?: number;
    keyword?: string;
    arrivalDate?: string;
    departureDate?: string;
    activeClaims?: boolean;
    organizationId?: number;
}

export interface GetClaimsDashboardParams {
    filter: OrderStatusFilter;
    page: number;
    pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class ClaimsService {

    lastSearchRequest?: SearchParams;

    constructor(
        private adminPortalApiHttpClient: AdminPortalAPIHttpClient,
        private snackbar: MatSnackBar,
        private logService: Logger
    ) { }
    
    getClaims = async ({ filter, page, pageSize, reservationId, arrivalDate, departureDate, activeClaims, organizationId }: GetClaimsParams): Promise<GetClaimsResponse> => {
        try {
            const optionalParams: any = {};
            if(arrivalDate) {
                optionalParams['from_date'] = arrivalDate;
            }
    
            if(departureDate) {
                optionalParams['end_date'] = departureDate;
            }
    
            if(reservationId) {
                optionalParams['reservation_id'] = reservationId;
            }
    
            if(activeClaims !== undefined) {
                optionalParams['is_active'] = Number(activeClaims);
            }
    
            if(organizationId) {
                optionalParams['organization_id'] = organizationId;
            }

            const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get('/claims', { params: 
                { 
                    limit: pageSize, 
                    p: page,
                    draft: Number(filter.draft), 
                    submitted: Number(filter.submitted), 
                    completed: Number(filter.completed), 
                    ...optionalParams
                } 
            }));
            if(!response) {
                return Promise.reject('Get claims response null');
            }

            return Promise.resolve({
                claims: response.data,
                totalRecords: response.total
            });
        } catch (e) {
            return Promise.reject(`Error while retrieving claims ${e}`);
        }
    }

    searchClaims = async (searchRequest: SearchParams): Promise<GetClaimsResponse> => {
        try {
            this.lastSearchRequest = searchRequest;

            const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post('/claims/search', {...searchRequest}));
            if(!response) {
                return Promise.reject('Search claims response null');
            }
    
            return Promise.resolve({
                claims: response.data,
                totalRecords: response.metaData.totalRecords
            });
        } catch (e) {
            return Promise.reject(`Error while searching claims: ${JSON.stringify(e)}`);
        }
    }

    getClaimById = async (claimId: string): Promise<Claim> => {
        try {
            const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/claims/${claimId}`));
            if(!response) {
                return Promise.reject(`Get claim response null ${claimId} `);
            }
            return Promise.resolve(response.data);
        } catch(e) {
            return Promise.reject(`Error while retrieving claim ${claimId} ${JSON.stringify(e)}`);
        }
    }

    getSelectedOrganizationClaimById = async (claimId: string): Promise<Claim> => {
        try {
            const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/claims/${claimId}`));
            if(!response) {
                return Promise.reject(`Get claim response null ${claimId} `);
            }
            return Promise.resolve(response.data);
        } catch(e) {
            return Promise.reject(`Error while retrieving claim ${claimId} ${JSON.stringify(e)}`);
        }
    }

    sendInquiry = async (claimId: string, email: string, questionAbout: string, comment: string) => {
        try {
            const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post('/claims/enquire', { claim_id: claimId, email: email, question_about: questionAbout, comment: comment }));
            if (response && !response.err) {
                this.snackbar.open('Inquiry Sent Successfully', 'OK', { duration: 4000 });
            } else {
                this.snackbar.open('Inquiry Unsuccessful, Please Try Again', 'OK', { duration: 4000 });
            }

            return Promise.resolve(response.data);
        } catch(e) {
            return Promise.reject(`Error while sending claim inquiry ${JSON.stringify(e)}`);
        }
    }

    async createClaim(claim: Claim): Promise<Claim> {
        try {
            const payload: Claim = {
                id: claim.id,
                reservation_id: claim.reservation_id,
                organization_id: claim.organization_id,
                property_id: claim.property_id,
                reservation_change: claim.reservation_change,
                estimate_amount: claim.estimate_amount,
                actual_amount: claim.actual_amount,
                send_check_to: claim.send_check_to,
                send_check_to_name: claim.send_check_to_name,
                send_check_to_phone: claim.send_check_to_phone,
                send_check_to_email: claim.send_check_to_email.trim(),
                send_check_to_address_id: claim.send_check_to_address_id,
                send_check_to_address: claim.send_check_to_address,
                damage_date: claim.damage_date,
                claim_cause_damage_rental_property: claim.claim_cause_damage_rental_property,
                claim_cause_damage_guest_property: claim.claim_cause_damage_guest_property,
                claim_cause_injury: claim.claim_cause_injury,
                claim_cause_theft_fire: claim.claim_cause_theft_fire,
                claim_cause_other: claim.claim_cause_other,
                injury_name: claim.injury_name,
                injury_phone: claim.injury_phone,
                injury_description: claim.injury_description,
                damage_description: claim.damage_description,
                home_liveable: claim.home_liveable,
                additional_comment: claim.additional_comment,
                admin_comment: claim.admin_comment,
                is_admin_comment_visible: claim.is_admin_comment_visible,
                status_id: claim.status_id,
                created_date: claim.created_date,
                updated_date: claim.updated_date,
                account_id: claim.account_id,
                is_active: claim.is_active,
                guest_rate: claim.guest_rate,
                would_rent_again: claim.would_rent_again,
                damage_accidental: claim.damage_accidental,
                claim_guest_party: claim.claim_guest_party,
                claim_guest_pets: claim.claim_guest_pets,
                claim_guest_disturbed_neighbours: claim.claim_guest_disturbed_neighbours,
                claim_guest_too_many_people: claim.claim_guest_too_many_people,
                claim_guest_stole: claim.claim_guest_stole,
                claim_other_issue: claim.claim_other_issue,
                claim_guest_other: claim.claim_guest_other,
                reservation: claim.reservation,
                property: claim.property,
                first_guest: claim.first_guest,
                status_history: claim.status_history,
                claim_documents: claim.claim_documents,
                currency_id: claim.currency_id,
                currency: claim.currency,
                injury_address_id: claim.injury_address_id,
                injury_address: {
                  id: claim.injury_address?.id,
                  address_line_1: claim.injury_address?.address_line_1,
                  address_line_2: claim.injury_address?.address_line_2,
                  city: claim.injury_address?.city,
                  state: claim.injury_address?.state,
                  postal_code: claim.injury_address?.postal_code,
                  country_id: claim.injury_address?.country_id,
                },
                goverment_department_id: claim.goverment_department_id,
                goverment_department: {
                  id: claim.goverment_department?.id,
                  name: claim.goverment_department?.name,
                  case_number: claim.goverment_department?.case_number,
                  officer_name: claim.goverment_department?.officer_name,
                  officer_phone: claim.goverment_department?.officer_phone
                }
            }

            const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post('/claims', payload));
            if(response != null && response.err != null && !isStringEmptyOrNull(response.err.msg)) {
                this.displaySnackbar(response.err.msg, claim.status_id);
            }
            return Promise.resolve(response.data);
        } catch (e) {
            return Promise.reject(`Error while creating claim ${JSON.stringify(e)}`);
        }
    }

    async updateClaimDraft(claimId: any, claim: any): Promise<Claim> {
        try {
            const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/claims/${claimId}`, claim));
            if (!response) {
                return Promise.reject(`Update claim draft response null ${claimId}`);
            } else {
                if(response.err != null && !isStringEmptyOrNull(response.err.msg)) {
                    this.displaySnackbar(response.err.msg, claim.status_id);
                }
            }

            return Promise.resolve(response.data);
        } catch (e) {
            return Promise.reject(`Error while updating claim draft ${claimId} ${JSON.stringify(e)}`);
        }
    }

    async updateClaimStatus(claimId: any, claim: any): Promise<Claim> {
        try {
            const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/claims/${claimId}`, claim));

            if (!response) {
                return Promise.reject(`Update claim status response null ${claimId}`);
            } else {
                if (response.err != null) {
                    this.displaySnackbar(response.err.msg, claim.status_id);
                } else {
                    this.displayStatusSuccessSnackbar(claimId);
                }
            }

            return Promise.resolve(response.data);
        } catch (e) {
            return Promise.reject(`Error while updating claim status ${claimId} ${JSON.stringify(e)}`);
        }
    }

    async updateClaimDocuments(claimId: string, organizationId: number, documents: ClaimDocument[]) {
        try {
            const payload = {
                id: claimId,
                organization_id: organizationId,
                claim_documents: documents.map(document => {
                    return {
                        id: document.id,
                        claim_id: claimId,
                        file_id: document.file_id,
                        description: document.description,
                        file_name: document.file_name,
                    }
                })
            };
            return await firstValueFrom(this.adminPortalApiHttpClient.post('/claims/documents/update', payload));
        } catch (e) {
            this.logService.handleError(e, `Error while updating Claim documents`, `Error while updating Claim documents ${claimId}`);
            return Promise.reject(`Error while updating claim documents ${claimId}`);
        }
    }

    displayStatusSuccessSnackbar(claimId: any) {
        this.snackbar.open(`Claim #${claimId} Update Successful`, 'OK', { duration: 4000 });
    }

    displaySnackbar(errorMessage: string, claimStatusId: number) {
        if (isStringEmptyOrNull(errorMessage)) {
            this.displaySuccessSnackbar(claimStatusId);
        } else {
            this.displayErrorSnackbar(errorMessage, claimStatusId)
        }
    }

    displaySuccessSnackbar(claimStatusId: number, claim?: any) {
        let message = '';
        if (claimStatusId === CLAIM_STATUS.DRAFT) {
            message = 'Save Successful';
        } else if (claimStatusId === CLAIM_STATUS.IN_PROGRESS) {
            message = 'Submit Successful';
        }

        if (message) {
            this.snackbar.open(message, 'OK', { duration: 4000 });
        }
    }

    displayErrorSnackbar(errorMessage: string, claimStatusId: number) {
        let message = '';
        if (claimStatusId === CLAIM_STATUS.DRAFT) {
            message = `Error Saving Claim: ${errorMessage}`;
        }
        if (claimStatusId === CLAIM_STATUS.IN_PROGRESS) {
            message = `Error Submitting Claim: ${errorMessage}`;
        }

        if (message) {
            this.snackbar.open(message, 'OK', { duration: 4000 });
        }
    }
}